import { useCallback, useMemo, useRef, useState } from 'react';
import { areaGroupDTOtoResource, IResource } from 'kits/apiKit3/legacy';
import { useStaredAreas, useWindowWidth } from 'shared-state/display-options/hooks';
import { useCurrentOrgId } from 'shared-state/location/hook';
import {
    useAreaGroupList,
    useAreaGroupListWithCount,
    useAreaList,
    useAreaListWithCount,
    useDeleteAreaOrAreaGroup,
    useRefetchAreaAndAreaGroupList
} from 'shared-state/directory/hooks';
import { areaDTOtoResource } from 'kits/apiKit3/legacy';
import { useCurrentCampusId, useCurrentRoomId } from 'shared-state/location/hook';
import { useBoolean } from '@uifabric/react-hooks';
import { resourceService } from 'services/api/resource.service';
import { useIdentityClaims } from 'shared-state/identity/hooks';
import { IIdentityClaims } from 'shared-state/identity/types';
import IvicosStrings from 'kits/language/stringKit';
import { IContextualMenuProps } from '@fluentui/react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { isValidAreaSortNumber } from './sortNumberValidation';

export interface IAreaListState {
    panelHeader: string;
    modalIsOpen: boolean;
    onAreaSelected: (areaId: string) => void;
    staredAreas: string[];
    onAreaStared: (a: IResource, s: boolean) => void;
    _areas: {
        isStared: boolean;
        id: string;
        type: string;
        attributes: any;
        relationships: any;
    }[];
    _areaGroups: {
        isStared: boolean;
        id: string;
        type: string;
        attributes: any;
        relationships: any;
    }[];
    campusId?: string;
    roomId?: string;
    isPanelOpen: boolean;
    openPanel: () => void;
    dismissPanel: () => void;
    setPanelHeader: React.Dispatch<React.SetStateAction<string>>;
    areaName: string;
    setAreaName: React.Dispatch<React.SetStateAction<string>>;
    areaTag: string;
    setAreaTag: React.Dispatch<React.SetStateAction<string>>;
    areaSortNumber: number | undefined;
    setAreaSortNumber: React.Dispatch<React.SetStateAction<number | undefined>>;
    /** `undefined` create/dismissed; `null` edit with no server sort (legacy); `number` edit snapshot at open. */
    sortNumberAtOpenSnapshot: number | null | undefined;
    seedAreaEditPanel: (areaId: string, areaResource: any, explicit?: { name?: string; tag?: string; sortNumber?: number }) => void;
    setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    deleteAreaOrAreaGroup: { id: string; name: string; parentAreaId?: string; isAreaGroup?: boolean };
    searchArea: (e: any, newValue: any) => void;
    searchInput: string;
    identityClaims?: IIdentityClaims;
    deleteAreaHandler: (deleteAreaOrAreaGroup: { id: string; name: string; parentAreaId?: string; isAreaGroup?: boolean }) => Promise<void>;
    /** `true` if create succeeded; `false` if validation prevented the request. Throws on API failure. */
    onCreateAreaHandler: () => Promise<boolean>;
    /** `true` if save succeeded; `false` if validation prevented the request. Throws on API failure. */
    onAreaChangeHandler: () => Promise<boolean>;
    onDismiss: () => void;
    areaMenuProps: IContextualMenuProps;
    isAddAreaGroupSelected: boolean;
    setIsAddAreaGroupSelected: React.Dispatch<React.SetStateAction<boolean>>;
    isCreatingOrEditingAreaOrGroup: boolean;
}

export const useAreaListState: (vertical?: boolean, onSelection?: Function, isAreaGroupRestricted?: boolean) => IAreaListState = (
    vertical,
    onSelection,
    isAreaGroupRestricted
) => {
    const { groupId, groupLevel } = useParams<{ groupId?: string; groupLevel?: string }>();
    const levelMap = {
        L0: 'L1',
        L1: 'L2'
    };
    const areaLevel = (groupLevel && levelMap[groupLevel]) || 'L0';
    const chosenLang = localStorage.getItem('chosenLanguage') || 'de';

    const { smallDevice, mediumDevice } = useWindowWidth();
    const [campusId] = useCurrentCampusId();
    const [roomId] = useCurrentRoomId();
    const [identityClaims] = useIdentityClaims();

    const [isPanelOpen, { setTrue: openPanel, setFalse: closePanel }] = useBoolean(false);
    const [panelHeader, setPanelHeader] = useState(IvicosStrings.createAreaTitle);
    const [modalIsOpen, setModalIsOpen] = useState(true);
    const [areaName, setAreaName] = useState('');
    const [areaTag, setAreaTag] = useState('');
    /** Default `0` for create; `seedAreaEditPanel` overwrites for edit. */
    const [areaSortNumber, setAreaSortNumber] = useState<number | undefined>(0);
    /** Mirrors ref: `undefined` not editing / create; `null` legacy missing sort; `number` value at edit open. */
    const [sortNumberAtOpenSnapshot, setSortNumberAtOpenSnapshot] = useState<number | null | undefined>(undefined);
    const sortNumberAtEditOpenRef = useRef<number | null | undefined>(undefined);
    const [currentOrgId] = useCurrentOrgId();
    const [staredAreas, setStaredAreas] = useStaredAreas(currentOrgId || 'default');
    const areasWithCount = useAreaListWithCount();
    const areaGroupsWithCount = useAreaGroupListWithCount();
    const [deleteAreaOrAreaGroup] = useDeleteAreaOrAreaGroup();
    const [, setAreaList] = useAreaList();
    const [areaGroupList, setAreaGroupList] = useAreaGroupList();
    const [searchInput, setSearchInput] = useState('');
    const [areaToEdit, setAreaToEdit] = useState('');
    const [isAddAreaGroupSelected, setIsAddAreaGroupSelected] = useState(false);
    const [, setRefetchAreaAndAreaGroupList] = useRefetchAreaAndAreaGroupList();
    const hideAreaGroups = isAreaGroupRestricted || smallDevice || mediumDevice;
    const [isCreatingOrEditingAreaOrGroup, setIsCreatingOrEditingAreaOrGroup] = useState(false);

    const _areas = useMemo(() => {
        const filteredAreas = areasWithCount
            .map((area: any) => areaDTOtoResource(area))
            .sort((a: any, b: any) => {
                const an = a.attributes?.sortNumber ?? Infinity;
                const bn = b.attributes?.sortNumber ?? Infinity;
                if (an !== bn) return an - bn;
                return (a.attributes?.displayName ?? '').toLowerCase().localeCompare((b.attributes?.displayName ?? '').toLowerCase());
            });

        return hideAreaGroups
            ? filteredAreas
            : filteredAreas
                  .map((area: any) => ({ ...area, ...{ isStared: staredAreas && staredAreas.includes(area.id) } }))
                  .filter((area: any) => area.attributes.level === areaLevel)
                  .filter((area: any) => !groupId || area.attributes.parentAreaId === groupId);
    }, [areasWithCount, groupId, areaLevel, staredAreas, hideAreaGroups]);

    const _areaGroups = useMemo(() => {
        return hideAreaGroups
            ? []
            : areaGroupsWithCount
                  .map((areaGroup: any) => areaGroupDTOtoResource(areaGroup))
                  .map((areaGroup: any) => ({ ...areaGroup, ...{ isStared: staredAreas && staredAreas.includes(areaGroup.id) } }))
                  .filter((areaGroup: any) => areaGroup.attributes.areaGroupLevel === areaLevel)
                  .sort((a: any, b: any) => {
                      const an = a.attributes?.sortNumber ?? Infinity;
                      const bn = b.attributes?.sortNumber ?? Infinity;
                      if (an !== bn) return an - bn;
                      return (a.attributes?.displayName ?? '').toLowerCase().localeCompare((b.attributes?.displayName ?? '').toLowerCase());
                  })
                  .filter((areaGroup: any) => !groupId || areaGroup.attributes.parentAreaId === groupId);
    }, [areaGroupsWithCount, groupId, areaLevel, staredAreas, hideAreaGroups]);

    const dismissPanel = () => {
        setAreaName('');
        setAreaTag('');
        setAreaSortNumber(0);
        sortNumberAtEditOpenRef.current = undefined;
        setSortNumberAtOpenSnapshot(undefined);
        setIsAddAreaGroupSelected(false);
        setIsCreatingOrEditingAreaOrGroup(false);
        closePanel();
    };

    const seedAreaEditPanel = useCallback((areaId: string, areaResource: any, explicit?: { name?: string; tag?: string; sortNumber?: number }) => {
        setAreaToEdit(areaId);
        const name = explicit?.name ?? areaResource?.attributes?.displayName ?? '';
        const tag = explicit?.tag ?? areaResource?.attributes?.areaLabel ?? areaResource?.attributes?.areaGroupLabel ?? '';
        const sn = explicit?.sortNumber ?? areaResource?.attributes?.sortNumber;
        setAreaName(name);
        setAreaTag(tag);
        if (typeof sn === 'number' && !Number.isNaN(sn)) {
            setAreaSortNumber(sn);
            sortNumberAtEditOpenRef.current = sn;
            setSortNumberAtOpenSnapshot(sn);
        } else {
            setAreaSortNumber(undefined);
            sortNumberAtEditOpenRef.current = null;
            setSortNumberAtOpenSnapshot(null);
        }
    }, []);

    const navigateToArea = (aid: string) => {
        window.location.href = '/areas/' + aid;
    };
    const onAreaSelected = (areaId: string) => {
        navigateToArea(areaId);
        onSelection && onSelection(areaId);
    };

    const onAreaStared = (a: IResource, s: boolean) => {
        if (s && staredAreas.length >= 2) {
            Swal.fire({
                title: IvicosStrings.maxFavoriteAreasReached.title,
                icon: 'warning',
                html: `
                <div style="text-align: justify; font-size: 14px;">
                    <p>${IvicosStrings.maxFavoriteAreasReached.description}</p>
                </div>
                `,
                confirmButtonText: IvicosStrings.maxFavoriteAreasReached.close,
                customClass: {
                    popup: 'custom-swal-font'
                },
                didOpen: (popup) => {
                    const titleElement = popup.querySelector('.swal2-title') as HTMLElement;
                    const closeButton = popup.querySelector('.swal2-confirm') as HTMLElement;

                    if (titleElement) {
                        titleElement.style.fontSize = '16px';
                        titleElement.style.fontWeight = '700';
                        titleElement.style.color = '#333';
                    }
                    if (closeButton) {
                        closeButton.style.backgroundColor = '#2b78d4';
                        closeButton.style.color = 'white';
                        closeButton.style.border = 'none';
                        closeButton.style.padding = '10px 20px';
                        closeButton.style.borderRadius = '5px';
                        closeButton.style.cursor = 'pointer';
                        closeButton.style.fontSize = '14px';
                    }
                }
            });
            return;
        }
        setStaredAreas((prevList: any) => {
            if (!s) return prevList.filter((areaId: any) => areaId != a.id);
            if (prevList && prevList.includes(a.id)) return prevList;
            if (!prevList) return [a.id];
            return [...prevList, ...[a.id]];
        });
    };

    const deleteAreaHandler = async (deleteAreaOrAreaGroup: { id: string; name: string; parentAreaId?: string; isAreaGroup?: boolean }) => {
        if (!campusId) return;
        const { id, parentAreaId, isAreaGroup } = deleteAreaOrAreaGroup || {};

        const updateParentAreaGroup = async (parentAreaId: string) => {
            if (!Array.isArray(areaGroupList)) {
                console.warn('areaGroupList is not an array');
                return;
            }

            const parentAreaGroup = areaGroupList.find((a: any) => a.id === parentAreaId);
            if (parentAreaGroup) {
                const updatedIncludedAreaIds = parentAreaGroup.includedAreaIds.filter((areaId: string) => areaId != id);

                // PATCH: only membership changes; name/groupTag/sortNumber omitted (unchanged on server).
                const updatedParentAreaGroup = await resourceService.updateAreaGroup(campusId, parentAreaId, {
                    includedAreaIds: updatedIncludedAreaIds
                });
                setAreaGroupList((prevList: any) => [...prevList, ...[updatedParentAreaGroup]]);
            } else {
                console.log('parentAreaGroup not found');
            }
        };

        if (isAreaGroup) {
            await resourceService.deleteAreaGroup(campusId, id);
            setAreaGroupList((prevList: any) => prevList.filter((a: any) => a.id != id));
        } else {
            await resourceService.deleteArea(campusId, id);
            setAreaList((prevList: any) => prevList.filter((a: any) => a.id != id));
        }
        setStaredAreas((prevList: any) => prevList.filter((areaId: any) => areaId != id));
        parentAreaId && (await updateParentAreaGroup(parentAreaId));
        setRefetchAreaAndAreaGroupList(true);
    };

    const searchArea = (e: any, newValue: any) => {
        typeof newValue == 'string' && setSearchInput(newValue);
    };

    const onDismiss = () => {
        setModalIsOpen(!modalIsOpen);
    };

    const onCreateAreaHandler = async (): Promise<boolean> => {
        if (!areaName || !campusId || !areaTag || areaSortNumber === undefined || !isValidAreaSortNumber(areaSortNumber)) return false;
        setIsCreatingOrEditingAreaOrGroup(true);
        try {
            const parentAreaId = groupId || undefined;
            if (isAddAreaGroupSelected) {
                const newAreaGroup = await resourceService.createAreaGroup(campusId, areaName, areaTag, areaLevel, chosenLang, areaSortNumber, parentAreaId);
                setAreaGroupList((prevList: any) => [...prevList, ...[newAreaGroup]]);
            } else {
                const newArea = await resourceService.createArea(campusId, areaName, areaTag, areaLevel, areaSortNumber, parentAreaId);
                setAreaList((prevList: any) => [...prevList, ...[newArea]]);
            }
            setRefetchAreaAndAreaGroupList(true);
            return true;
        } finally {
            setIsCreatingOrEditingAreaOrGroup(false);
        }
    };

    const onAreaChangeHandler = async (): Promise<boolean> => {
        if (!campusId) return false;

        const areaGroupForEdit = isAddAreaGroupSelected ? areaGroupsWithCount.find((a) => a.id === areaToEdit) : undefined;
        if (isAddAreaGroupSelected && !areaGroupForEdit) return false;

        const snapshot = sortNumberAtEditOpenRef.current;
        if (snapshot === undefined) {
            if (areaSortNumber === undefined || !isValidAreaSortNumber(areaSortNumber)) return false;
        } else if (snapshot === null) {
            if (areaSortNumber !== undefined && !isValidAreaSortNumber(areaSortNumber)) return false;
        } else {
            if (areaSortNumber === undefined || !isValidAreaSortNumber(areaSortNumber)) return false;
        }

        setIsCreatingOrEditingAreaOrGroup(true);
        try {
            let sortNumberChanged = false;
            if (typeof snapshot === 'number') {
                sortNumberChanged = areaSortNumber !== snapshot;
            } else if (snapshot === null) {
                sortNumberChanged = areaSortNumber !== undefined && isValidAreaSortNumber(areaSortNumber);
            }

            if (isAddAreaGroupSelected && areaGroupForEdit) {
                const groupChanges: { name?: string; tag?: string; sortNumber?: number; includedAreaIds?: string[] } = {
                    name: areaName || undefined,
                    tag: areaTag || undefined,
                    includedAreaIds: areaGroupForEdit.includedAreaIds
                };
                if (sortNumberChanged) groupChanges.sortNumber = areaSortNumber;
                await resourceService.updateAreaGroup(campusId, areaToEdit, groupChanges);
                setAreaGroupList((prevList: any) =>
                    prevList.map((ag: any) => {
                        if (ag.id != areaToEdit) return ag;
                        const updates: any = {};
                        if (areaName) updates.name = areaName;
                        if (areaTag) updates.groupTag = areaTag;
                        if (sortNumberChanged) updates.sortNumber = areaSortNumber;
                        return { ...ag, ...updates };
                    })
                );
            } else {
                const areaChanges: { name?: string; tag?: string; sortNumber?: number } = {
                    name: areaName || undefined,
                    tag: areaTag || undefined
                };
                if (sortNumberChanged) areaChanges.sortNumber = areaSortNumber;
                await resourceService.updateArea(campusId, areaToEdit, areaChanges);
                setAreaList((prevList: any) =>
                    prevList.map((area: any) => {
                        if (area.id != areaToEdit) return area;

                        const updates: any = {};
                        if (areaName) updates.name = areaName;
                        if (areaTag) updates.tag = areaTag;
                        if (sortNumberChanged) updates.sortNumber = areaSortNumber;
                        return { ...area, ...updates };
                    })
                );
            }
            setRefetchAreaAndAreaGroupList(true);
            return true;
        } finally {
            setIsCreatingOrEditingAreaOrGroup(false);
        }
    };

    const areaMenuProps: IContextualMenuProps = {
        items: [
            {
                key: 'addAnArea',
                text: IvicosStrings.addAnAreaActionTitle,
                iconProps: { iconName: 'Add' },
                onClick: () => {
                    openPanel();
                    setIsAddAreaGroupSelected(false);
                    setPanelHeader(IvicosStrings.createAreaTitle);
                    setAreaSortNumber(0);
                    sortNumberAtEditOpenRef.current = undefined;
                    setSortNumberAtOpenSnapshot(undefined);
                }
            },
            {
                key: 'addAreaGroup',
                text: IvicosStrings.addAreaGroupActionTitle,
                iconProps: { iconName: 'BranchMerge' },
                disabled: areaLevel === 'L2',
                onClick: () => {
                    openPanel();
                    setIsAddAreaGroupSelected(true);
                    setPanelHeader(IvicosStrings.createAreaGroupTitle);
                    setAreaSortNumber(0);
                    sortNumberAtEditOpenRef.current = undefined;
                    setSortNumberAtOpenSnapshot(undefined);
                }
            }
        ]
    };

    return {
        onAreaSelected,
        staredAreas,
        onAreaStared,
        _areas,
        _areaGroups,
        campusId,
        roomId,
        isPanelOpen,
        openPanel,
        dismissPanel,
        panelHeader,
        setPanelHeader,
        areaName,
        setAreaName,
        areaTag,
        setAreaTag,
        areaSortNumber,
        setAreaSortNumber,
        sortNumberAtOpenSnapshot,
        seedAreaEditPanel,
        modalIsOpen,
        setModalIsOpen,
        deleteAreaOrAreaGroup,
        searchArea,
        searchInput,
        identityClaims,
        deleteAreaHandler,
        onCreateAreaHandler,
        onAreaChangeHandler,
        onDismiss,
        areaMenuProps,
        isAddAreaGroupSelected,
        setIsAddAreaGroupSelected,
        isCreatingOrEditingAreaOrGroup
    };
};
